import React, { useEffect, useState } from 'react';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { scrollToTop } from '~/helpers/helperFunction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { cn } from '~/helpers/utils';

interface PaginationProps {
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  initialPage?: number;
  urlPath?: string;
  urlQuery?: string;
  pageQueryName?: string;
}

const Pagination: React.FC<PaginationProps> = (props: PaginationProps) => {
  const { itemsPerPage, totalItems, onPageChange, initialPage, urlPath, urlQuery, pageQueryName } = props;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(initialPage ?? 1);

  const handlePageChange = (page: number) => {
    scrollToTop();
    setCurrentPage(page);
    onPageChange(page);
  };

  const handlePageChangeOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: number) => {
    event.preventDefault();
    handlePageChange(page);
  };

  const createHref = (page: number) => {
    const queryParam = pageQueryName ?? 'page';
    let queryString = `?${queryParam}=${page.toString()}`;

    if (urlQuery !== undefined && urlQuery.trim() !== '') {
      queryString = urlQuery.includes(queryParam) 
        ? urlQuery.replace(new RegExp(`('${queryParam}'=)[^\&]+`), `$1${page.toString()}`)
        : `${urlQuery}&${queryParam}=${page.toString()}`;
    }

    return `${urlPath || ''}${queryString}`
  }

  const renderPageButton = (pageNumber: number) => (
    <a
      type="button"
      key={pageNumber}
      href={createHref(pageNumber)}
      onClick={e => handlePageChangeOnClick(e, pageNumber)}
      className={cn(
        'pagination-link',
        pageNumber === currentPage ? 'pagination-link--current' : '',
      )}
    >
      {pageNumber}
    </a>
  );

  const renderEllipsis = (index: number) => <span className="ellipsis flex items-center justify-center h-8 px-2 text-stone-700" key={index}>...</span>;

  const renderPaginationButtons = () => {
    const delta = 1;
    const range = new Set<number>();

    for (let i = 1; i <= Math.min(3, totalPages); i++) range.add(i);
    for (let i = Math.max(1, totalPages - 2); i <= totalPages; i++) range.add(i);
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) range.add(i);

    const pages = Array.from(range).sort((a, b) => a - b);
    const buttons: React.ReactNode[] = [];

    pages.forEach((page, idx) => {
      if (idx > 0 && page - pages[idx - 1] > 1) {
        buttons.push(renderEllipsis(page + 1000));
      }
      buttons.push(renderPageButton(page));
    });

    return buttons;
  };

  useEffect(() => {
    if (currentPage === totalPages || currentPage === 1) {
      scrollToTop();
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (initialPage) {
      setCurrentPage(initialPage);
    }
  }, []);

  return (
    <div data-testid="pagination-component" className="flex flex-col text-center lg:justify-between lg:flex-row gap-3 items-center w-full">
      <div className="font-body font-normal md:text-base text-sm">
        <span className="font-black">
          {currentPage === 1 ? 1 : currentPage * itemsPerPage - itemsPerPage}{' '}
          t/m{' '}
          {totalItems < currentPage * itemsPerPage
            ? totalItems
            : currentPage * itemsPerPage}
        </span>{' '}
        van {totalItems}
      </div>
      <div className="pagination flex justify-center flex-wrap lg:w-[70%] lg:justify-end w-[100%] gap-2">
        <button
          className={cn(
            'pagination-button',
            currentPage === 1 ? 'pagination-button--disabled' : '',
          )}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Vorige"
        >
          <FontAwesomeIcon icon={faAngleLeft as IconProp} />
        </button>
        {renderPaginationButtons()}
        <button
          className={cn(
            'pagination-button',
            currentPage === totalPages ? 'pagination-button--disabled' : '',
          )}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Volgende"
        >
          <FontAwesomeIcon icon={faAngleRight as IconProp} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
